import type { LandingFeature } from '@/types/landingCatalog.types'

/** Full feature list for /funcionalidades (Escritura). Order: planificación → editor → hábitos → versiones → exportar. */
export const LANDING_WRITE_CATALOG_FEATURES: LandingFeature[] = [
  {
    id: 'plot',
    icon: 'mdi-sitemap',
    title: 'Organizador de tramas',
    description: 'Estructura la historia y desarrolla sus arcos sin perder de vista el conjunto.',
  },
  {
    id: 'whiteboard',
    icon: 'mdi-draw',
    title: 'Pizarra',
    description: 'Ordena ideas sueltas y marca las conexiones entre ellas.',
  },
  {
    id: 'maps',
    icon: 'mdi-map',
    title: 'Mapas',
    description: 'Sube tu mapa y define las localizaciones de tu historia.',
  },
  {
    id: 'fichas',
    icon: 'mdi-card-account-details',
    title: 'Fichas',
    description: 'Guarda lo que sabes de personajes, lugares y objetos en un solo lugar.',
  },
  {
    id: 'relations',
    icon: 'mdi-graph',
    title: 'Árbol de relaciones',
    description: 'Mapa visual de conexiones entre elementos narrativos.',
  },
  {
    id: 'notes',
    icon: 'mdi-note-text',
    title: 'Notas',
    description: 'Anota ideas y observaciones antes de que se te escapen.',
  },
  {
    id: 'todos',
    icon: 'mdi-checkbox-marked-outline',
    title: 'Pendientes',
    description: 'Lleva las tareas del proyecto junto a la historia.',
  },
  {
    id: 'timelines',
    icon: 'mdi-timeline',
    title: 'Líneas de tiempo',
    description: 'Cronologías con eventos fechados para mantener coherencia temporal.',
  },
  {
    id: 'hierarchies',
    icon: 'mdi-file-tree',
    title: 'Jerarquías',
    description: 'Organigramas de puestos y áreas; vincula fichas a cada puesto.',
  },
  {
    id: 'editor',
    icon: 'mdi-text-box-edit',
    title: 'Editor',
    description: 'Escribe la obra en el espacio pensado para quedarte en la página.',
  },
  {
    id: 'concentration',
    icon: 'mdi-eye-off-outline',
    title: 'Concentración',
    description: 'Reduce lo que distrae y quédate con el texto.',
  },
  {
    id: 'comments',
    icon: 'mdi-comment-text-outline',
    title: 'Comentarios del capítulo',
    description: 'Notas y comentarios asociados al capítulo que estás editando.',
  },
  {
    id: 'writing-alerts',
    icon: 'mdi-alert-outline',
    title: 'Análisis mientras escribes',
    description:
      'Marcas en el texto para repeticiones, frases largas y ritmo repetido mientras redactas.',
  },
  {
    id: 'editorial-analysis',
    icon: 'mdi-chart-bar',
    title: 'Análisis editorial',
    description:
      'Métricas del manuscrito: ritmo, palabras, diálogo y comparación entre capítulos.',
  },
  {
    id: 'screenplay',
    icon: 'mdi-movie-open',
    title: 'Formato de guion',
    description: 'Bloques especializados para escenas, diálogos y acotaciones.',
  },
  {
    id: 'streak',
    icon: 'mdi-fire',
    title: 'Racha',
    description:
      'Lleva tus días seguidos escribiendo, revisa el calendario y celebra hitos con la estrella del día.',
  },
  {
    id: 'goals',
    icon: 'mdi-star',
    title: 'Metas diarias',
    description: 'Mantente motivado a escribir cada día.',
  },
  {
    id: 'challenges',
    icon: 'mdi-flag-checkered',
    title: 'Desafíos mensuales',
    description:
      'Un objetivo mensual que se adapta a tu ritmo: palabras, tiempo, días escritos o estrellas.',
  },
  {
    id: 'writing-feedback',
    icon: 'mdi-chart-box-outline',
    title: 'Feedback de hábitos',
    description: 'Resumen del mes con las palabras que más usaste al escribir.',
  },
  {
    id: 'versions',
    icon: 'mdi-history',
    title: 'Versiones',
    description:
      'Respaldos automáticos al escribir y versiones manuales para congelar un hito o recuperar un borrador.',
  },
  {
    id: 'beta-readers',
    icon: 'mdi-account-group-outline',
    title: 'Lectores beta',
    description: 'Comparte una versión congelada y recibe comentarios de lectores.',
  },
  {
    id: 'exporter',
    icon: 'mdi-microsoft-word',
    title: 'Exportación rápida a Word',
    description: 'Genera un .docx con la configuración del editor, sin maquetación editorial.',
  },
  {
    id: 'maquetador',
    icon: 'mdi-layers-triple',
    title: 'Maquetador',
    description:
      'Maquetación editorial: estructura del libro, estilo tipográfico y exportación a PDF, EPUB o Word.',
  },
  {
    id: 'import-docx',
    icon: 'mdi-file-word-box',
    title: 'Importar Word (.docx)',
    description:
      'Trae un manuscrito .docx al proyecto; revisas cada bloque detectado antes de confirmar.',
  },
]

/** Full feature list for /funcionalidades (Comunidad). */
export const LANDING_COMMUNITY_CATALOG_FEATURES: LandingFeature[] = [
  {
    id: 'profile',
    icon: 'mdi-account-circle-outline',
    title: 'Perfil público',
    description: 'Preséntate, arma tu identidad y muestra tu actividad literaria.',
  },
  {
    id: 'capsule',
    icon: 'mdi-cube-outline',
    title: 'Cápsula',
    description: 'Publica piezas de lo que estás creando y de tu vida como escritor.',
  },
  {
    id: 'snapshots',
    icon: 'mdi-share-variant-outline',
    title: 'Snapshots compartibles',
    description: 'Comparte una instantánea de tu contenido con quien quieras.',
  },
  {
    id: 'reader-comments',
    icon: 'mdi-comment-text-outline',
    title: 'Comentarios de lectores',
    description: 'Quienes leen pueden dejar un comentario en la historia.',
  },
  {
    id: 'activity',
    icon: 'mdi-newspaper-variant-outline',
    title: 'Actividad',
    description:
      'Sigue a otros escritores, revisa logros y descubre quién escribe en la comunidad.',
  },
  {
    id: 'groups',
    icon: 'mdi-account-multiple-outline',
    title: 'Grupos',
    description: 'Participa en espacios para conversar con otros escritores.',
  },
  {
    id: 'group-forum',
    icon: 'mdi-forum-outline',
    title: 'Foro de grupo',
    description: 'Publica hilos, comenta y reacciona con otros escritores del grupo.',
  },
  {
    id: 'group-chats',
    icon: 'mdi-chat-outline',
    title: 'Chats',
    description: 'Conversa con otros miembros cuando quieras seguir la charla.',
  },
  {
    id: 'writing-sprints',
    icon: 'mdi-timer-outline',
    title: 'Sprints de escritura',
    description:
      'Sesiones en grupo con duración y meta de palabras para escribir juntos en el editor.',
  },
]
